import {
    BUBBLE,
    BUBBLE_DASHED,
    BUBBLE_GHOST,
    BUBBLE_IDLE,
    BUBBLE_ON,
    FIELD,
} from "@client/components/styles";
import {Label, TextInput} from "@client/forms";
import * as hardware from "@client/hardware";
import {classNames, server} from "@reactivated";
import {includes} from "lodash-es";
import * as React from "react";

// Always put Breakfast, Lunch, Dinner, Snack first, then sort alphabetically.
const sortMeals = <T extends {label: string}>(a: T, b: T) => {
    const order = ["Breakfast", "Lunch", "Dinner", "Snack"];
    const indexA = order.indexOf(a.label);
    const indexB = order.indexOf(b.label);

    if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
    } else if (indexA !== -1) {
        return -1;
    } else if (indexB !== -1) {
        return 1;
    } else {
        return a.label.localeCompare(b.label);
    }
};

// We don't want these shifting around, especially during a quick cycle of
// add another etc (recent meals come back sorted by recency).
export const getMealOptionsForDay = (day: server.journal.nutrition.Day.output) => {
    const dayMeals = day.meals
        .filter((m) => m.name != "__SUPPLEMENTS__")
        .filter((m) => m.type != "AUTO_CREATED")
        .map((m) => ({
            value: m.name,
            label: m.name,
        }));
    const dayNames = dayMeals.map((m) => m.label);
    const recentMeals = day.recent_meals
        .filter((name) => name != "__SUPPLEMENTS__")
        .map((name) => ({
            value: name,
            label: name,
        }))
        .filter((m) => !includes(dayNames, m.label));

    return {
        allMeals: dayMeals
            .concat(recentMeals)
            .slice()
            .sort(sortMeals)
            .map((m) => m.value)
            .reduce<string[]>((acc, m) => {
                if (!includes(acc, m)) {
                    acc.push(m);
                }
                return acc;
            }, [])
            .map((m) => ({value: m, label: m})),
    };
};

// `dashed` is the indigo outline for "+ New"; `ghost` the gray outline for a
// secondary shortcut like "I ate the whole thing".
export const Bubble = (props: {
    label: string;
    selected: boolean;
    dashed?: boolean;
    ghost?: boolean;
    disabled?: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        disabled={props.disabled}
        aria-pressed={props.selected}
        onClick={() => {
            void hardware.haptic();
            props.onClick();
        }}
        className={classNames(
            BUBBLE,
            "disabled:opacity-40",
            props.selected
                ? BUBBLE_ON
                : props.dashed === true
                  ? BUBBLE_DASHED
                  : props.ghost === true
                    ? BUBBLE_GHOST
                    : BUBBLE_IDLE,
        )}
    >
        {props.label}
    </button>
);

interface Props {
    day: server.journal.nutrition.Day.output;
    onMealChange: (meal: string | null) => void;
    value: string | null;
    error: string | null;
}

// The day's meals plus recent names as bubbles, with a dashed "+ New" that
// opens the name field. Above eight the bubbles wrap; that is fine.
export function MealSelector(props: Props) {
    const {allMeals} = getMealOptionsForDay(props.day);
    const isExisting = allMeals.some((meal) => meal.value === props.value);
    const [creating, setCreating] = React.useState(
        props.value == null || props.value === "" || !isExisting,
    );

    return (
        <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap gap-1.5">
                {allMeals.map(({label, value}) => (
                    <Bubble
                        key={value}
                        label={label}
                        selected={!creating && props.value === value}
                        onClick={() => {
                            setCreating(false);
                            props.onMealChange(value);
                        }}
                    />
                ))}
                <Bubble
                    label="+ New"
                    selected={creating}
                    dashed
                    onClick={() => {
                        setCreating(true);
                        props.onMealChange(null);
                    }}
                />
            </div>
            {creating && (
                <Label label={null} error={props.error}>
                    <TextInput
                        name="meal_name"
                        ariaLabel="Meal name"
                        placeholder="Meal name"
                        className={classNames(
                            FIELD,
                            "h-[38px] w-full px-3.5 text-[14px] font-normal outline-none focus:ring-[1.5px] focus:ring-[oklch(0.51_0.21_278_/_0.3)]",
                        )}
                        readOnly={false}
                        onChange={props.onMealChange}
                        value={props.value}
                    />
                </Label>
            )}
        </div>
    );
}
